import { Alert, Box, Button, Loader, Table } from '@plusplusminus/plusplusdash'
import { Link, RouteComponentProps } from '@reach/router'
import DebouncedSearchInput from 'components/DebouncedSearchInput'
import { useSizingGuidesQuery, useDeleteSizingGuideMutation } from 'hooks/sizingGuides'
import { useState } from 'react'
import toast from 'react-hot-toast'
import { DeleteModal } from 'components/Modals/Delete'
import { Pagination } from 'components/Pagination'
import dayjs from 'dayjs'

const TABLE_SHAPE = [
  { label: 'Name', key: 'name', isSortable: true },
  { label: 'Created At', key: 'createdAt', isSortable: true },
  { label: '', key: '', isSortable: false }
]

export const SizingGuides: React.FC<RouteComponentProps> = () => {
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const { query, pagination, sort, filtering } = useSizingGuidesQuery({ persist: true })
  const { refetch, loading, error, data, previousData } = query
  const { page, nextPage, prevPage } = pagination
  const [sortField, direction, sortCallback] = sort
  const { filter, setFilter, removeFilter } = filtering

  const { deleteSizingGuide, loading: loadingDelete } = useDeleteSizingGuideMutation({
    onCompleted: () => {
      toast.success('Sizing guide deleted')
      setDeleteId(null)
      refetch()
    },
    onError: () => toast.error('Could not delete sizing guide')
  })

  const tableData = data || previousData

  return (
    <Box className="flex flex-col">
      <Box className="flex p-5 justify-between items-center bg-white border-b border-gray-200">
        <h1 className="text-lg">Sizing Guides</h1>
        <Link to="/sizing-guides/create">
          <Button type="button" variant="primary" size="sm" colorScheme="blue">
            New Sizing Guide
          </Button>
        </Link>
      </Box>
      {error ? (
        <Alert type="error">
          <Alert.Heading>Error fetching sizing guides</Alert.Heading>
        </Alert>
      ) : !previousData && loading ? (
        <div className="flex flex-auto justify-center items-center">
          <Loader isActive={true} />
        </div>
      ) : tableData ? (
        <Box>
          <div className="bg-white w-full flex py-2 px-5">
            <DebouncedSearchInput
              onRemoveFilter={() => removeFilter('name')}
              as="input"
              variant="outline"
              width="md"
              id="name"
              name="name"
              onChange={(e) => setFilter('name', e.target.value)}
              value={filter.name ?? ''}
              placeholder="Search sizing guides"
            />
          </div>
          <DeleteModal
            headline="Delete Sizing Guide"
            description="Are you sure you want to delete this sizing guide? Any brands using it will lose their guide."
            isModalOpen={!!deleteId}
            isLoading={loadingDelete}
            onClose={() => setDeleteId(null)}
            onDelete={() => deleteId && deleteSizingGuide(deleteId)}
          />
          <Table
            shape={TABLE_SHAPE}
            sortCallback={sortCallback}
            activeSort={sortField}
            sortDirection={direction?.toLowerCase()}
          >
            {tableData.sizingGuides.edges.map(({ node: guide }) => (
              <Table.Row key={guide.id}>
                <Table.Cell className="font-medium text-gray-900">{guide.name}</Table.Cell>
                <Table.Cell>{dayjs(guide.createdAt).format('DD-MM-YYYY')}</Table.Cell>
                <Table.Cell className="text-right flex gap-4 justify-end">
                  <Link className="underline" to={`/sizing-guides/${guide.id}`}>
                    Edit
                  </Link>
                  <button type="button" className="underline text-red-500" onClick={() => setDeleteId(guide.id)}>
                    Delete
                  </button>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table>
          <Pagination
            hasPreviousPage={tableData.sizingGuides.pageInfo.hasPreviousPage}
            hasNextPage={tableData.sizingGuides.pageInfo.hasNextPage}
            onNext={nextPage}
            onPrev={prevPage}
            page={page}
          />
        </Box>
      ) : null}
    </Box>
  )
}
